import {
  TextEvent,
  ToolStatusEvent,
  ExternalToolEvent,
  ErrorEvent,
  FinalEvent,
  YieldEvent,
  ReasoningEvent,
  RefusalEvent,
  ToolApprovalRequiredEvent,
  ToolApprovalResolvedEvent,
  ToolApprovalDecisionAckEvent,
  ToolResultAckEvent,
  UserInputEvent,
  ToolPartialEvent,
  EventExecutionContext,
} from '@packages/systems/ai/agent/shared';
import type {ChatModel} from '@packages/systems/ai/shared';
import type {WorkflowType} from '@packages/systems/core/records/DesignerState';
import type {
  CampaignPageTargetLanguage,
  StructuredBrief,
} from '@packages/domains/campaigns/data-types';

/**
 * Maximum length in characters of a user-authored assistant message.
 * Enforced by the POST message Ajv body schema on the server and mirrored on the client to disable the submit button.
 */
export const MAX_USER_MESSAGE_LENGTH = 25_000;

export const MAX_CAMPAIGN_BRIEF_CONTEXT_LENGTH = 8_000;

/**
 * MIME types the client accepts from the user before processing.
 * Images are converted to WebP before upload, so this is broader than
 * what the presigned-URL endpoint accepts.
 */
export const ACCEPTED_INPUT_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
] as const;

/**
 * MIME types accepted by the presigned-URL endpoint on the server.
 * All uploads are converted to WebP on the client before sending, so only
 * webp needs to be allowed here.
 */
export const ALLOWABLE_ASSISTANT_FILE_UPLOAD_TYPES = ['image/webp'] as const;

export type AllowableFileUploadType =
  (typeof ALLOWABLE_ASSISTANT_FILE_UPLOAD_TYPES)[number];

export interface AIAssistantConversation {
  _id: string;
  siteId: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  metadata: Record<string, unknown>;
}

export interface AIAssistantConversationForClient {
  id: string;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown>;
}

export const AIA_ASSISTANT_OPEN_SOURCES = {
  AISB_AUTO_OPEN: 'aisb_auto_open',
  ASSISTANT_TOGGLE_BUTTON: 'assistant_toggle_button',
  ASSISTANT_USER_INPUT: 'assistant_user_input',
  AUTO_REOPEN_FROM_LAST_SESSION: 'auto_reopen_from_last_session',
  CAMPAIGN_AUTO_OPEN: 'campaign_auto_open',
  CODE_COMPONENT_FIX_WITH_AI: 'code_component_fix_with_ai',
  CODE_COMPONENT_FIX_STRUCTURE: 'code_component_fix_structure',
  COMPONENT_STATES_GENERATE: 'component_states_generate',
  KEYBOARD_SHORTCUT: 'keyboard_shortcut',
  VCC_STYLES_PANEL: 'vcc_styles_panel',
  VCC_CONTEXT_TOOLBAR: 'vcc_context_toolbar',
  INSTRUCTIONS_EDIT_WITH_AIA: 'instructions_edit_with_aia',
  INSTRUCTIONS_CREATE_WITH_AI: 'instructions_create_with_ai',
  CAMPAIGN_EMPTY_PAGE_SLOT: 'campaign_empty_page_slot',
  CAMPAIGN_LEFT_RAIL_ADD: 'campaign_left_rail_add',
  CAMPAIGN_QUICK_ADD: 'campaign_quick_add',
  CAMPAIGN_RIGHT_PANEL_ADD: 'campaign_right_panel_add',
} as const;

export type AIAssistantOpenSource =
  (typeof AIA_ASSISTANT_OPEN_SOURCES)[keyof typeof AIA_ASSISTANT_OPEN_SOURCES];

export const AIA_ASSISTANT_OPEN_METADATA_FIELDS = {
  TRIGGERED_POST_AISB: 'aia_triggered_post_aisb',
  OPEN_AT_LAST_SESSION_END: 'aia_open_at_last_session_end',
} as const;

export const AIA_ASSISTANT_OPEN_METADATA_KEYS = [
  AIA_ASSISTANT_OPEN_METADATA_FIELDS.TRIGGERED_POST_AISB,
  AIA_ASSISTANT_OPEN_METADATA_FIELDS.OPEN_AT_LAST_SESSION_END,
] as const;

export type AIAssistantOpenMetadataKey =
  (typeof AIA_ASSISTANT_OPEN_METADATA_KEYS)[number];

export type AIAssistantOpenMetadata = Partial<
  Record<AIAssistantOpenMetadataKey, boolean>
>;

export interface AIAssistantSession {
  _id: string;
  siteId: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  metadata: Record<string, unknown>;
}

export interface AIAssistantOpenState {
  siteId: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  metadata: Record<string, unknown>;
}

export interface ClientTextEvent extends TextEvent {
  role: 'user' | 'assistant';
}

export interface ExternalToolRequestEvent extends ExternalToolEvent {
  type: 'external_tool_call';
  expectedVersions?: Partial<Record<VersionDimension, DimensionVersion>>;
}

export interface ExternalToolResponseEvent extends ExternalToolEvent {
  type: 'external_tool_response';
  designerContext?: AssistantDesignerContext;
  model?: ChatModel;
  versionInfo?: VersionInfo;
}

export interface CompiledCodeBundleAttachmentPayload {
  componentId: string;
  compiledAt: string;
  bundle: {
    js: string;
    css: string;
    dependencies: string[];
    evaluatedVersion: number;
  };
}

export interface InterruptedEvent {
  id: string;
  type: 'interrupted';
  spanId?: string;
  execution?: EventExecutionContext;
}

export type ClientFrameworkEvent =
  | ClientTextEvent
  | ReasoningEvent
  | RefusalEvent
  | ToolStatusEvent
  // HITL functionality removed
  | ExternalToolRequestEvent
  | ToolApprovalRequiredEvent
  | ToolApprovalResolvedEvent
  | ToolResultAckEvent
  | ToolApprovalDecisionAckEvent
  | ExternalToolResponseEvent
  | ErrorEvent
  | FinalEvent
  | YieldEvent
  | UserInputEvent
  | ToolPartialEvent
  | InterruptedEvent;

/**
 * Runtime set of event type strings that correspond to ClientFrameworkEvent.
 * Keep in sync with the union above.
 */
export const CLIENT_FRAMEWORK_EVENT_TYPES: ReadonlySet<string> = new Set([
  'text',
  'reasoning',
  'refusal',
  'tool_status',
  'external_tool_call',
  'tool_approval_required',
  'tool_approval_resolved',
  'tool_result_accepted',
  'tool_result_duplicate_accepted',
  'tool_result_ignored',
  'tool_result_conflict',
  'tool_approval_decision_accepted',
  'tool_approval_decision_duplicate_accepted',
  'tool_approval_decision_ignored',
  'tool_approval_decision_conflict',
  'external_tool_response',
  'error',
  'final',
  'yield',
  'user_input',
  'tool_partial',
  'interrupted',
]);

export type AssistantStreamEvent =
  | {
      name: 'start';
    }
  | {
      name: 'end';
    }
  | {
      name: 'agent-event';
      data: ClientFrameworkEvent;
    };

export interface AssistantBreakpoint {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
}

export interface AssistantCmsCollectionField {
  slug: string;
  type: string;
  name: string;
  required?: boolean;
}

export interface AssistantCmsCollection {
  id: string;
  name: string;
  fields: AssistantCmsCollectionField[];
}

export type CampaignComponentTask =
  | {
      kind: 'campaign-component-creation';
      phase: 'kickoff' | 'build';
      source: 'campaign-empty-page-slot' | 'campaign-left-rail-add';
      placement: {
        pageSlotId: string;
        position: 'append';
      };
    }
  | {
      kind: 'campaign-component-creation';
      phase: 'kickoff' | 'build';
      source: 'campaign-quick-add';
      placement: {
        pageSlotId: string;
        anchorInstanceId: string;
        position: 'before' | 'after';
      };
    };

export const DEFAULT_ASSISTANT_BREAKPOINTS: AssistantBreakpoint[] = [
  {id: 'main', label: 'Desktop'},
  {id: 'medium', label: 'Tablet', maxWidth: 991},
  {id: 'small', label: 'Mobile (L)', maxWidth: 767},
  {id: 'tiny', label: 'Mobile', maxWidth: 479},
];

/**
 * Designer abilities forwarded verbatim as Campaign UI capabilities. Each name
 * must be a `designer-abilities` selector name.
 */
export const CAMPAIGN_UI_ABILITIES = [
  'isInCampaignBuildMode',
  'isInCampaignFullMode',
  'canToggleCampaignSiteMode',
  'canAccessNavigator',
  'canModifyStyleBlocks',
  'canCreateStyleBlocks',
  'canAccessInteractionsPanel',
  'canEditContent',
  'canAddElementToCanvas',
  'canReorderElements',
  'canRemoveElement',
  'canModifyComponentsOnCanvas',
  'canManageComponents',
  'canConnectProperties',
  'canAccessAssets',
  'canManageAssets',
  'canAccessVariablesPanel',
  'canModifyVariables',
  'canEditBrandPanel',
  'canLaunchCampaign',
] as const;

/**
 * Designer UI ability signals captured on Campaign sites (site kind
 * `campaign`). Surfaced so the assistant can tailor guidance to what the
 * user can currently do in the UI — these are UI signals, NOT tool
 * authorization grants.
 *
 * Omitted entirely for ordinary (non-Campaign) sites; `false` values are
 * preserved so the assistant can distinguish "hidden" from "allowed".
 *
 * `canAccessStylePanel` has no standalone ability: it is derived as
 * `!isInPageBuildMode && canDesign`. `!isInPageBuildMode` matches the
 * RightSidebar style-panel branch; `canDesign` is an additional conservative
 * gate so view-only sessions aren't told to edit styles.
 */
export type CampaignUiCapabilities = Record<
  (typeof CAMPAIGN_UI_ABILITIES)[number] | 'canAccessStylePanel',
  boolean
>;

export interface AssistantDesignerContext {
  supportsExactTextEdit?: boolean;
  selectedElement: {
    id: string;
    type: string;
  } | null;
  workflow: WorkflowType;
  currentPage: {
    id: string;
    name: string;
    slug: string;
    parentId: string | null | undefined;
    type: string | null | undefined;
    isComponentPage?: boolean;
    /** The component a component page renders; null on regular pages. */
    componentId?: string | null;
  } | null;
  codeComponent: {
    id: string;
    code: string;
    displayName?: string;
    templateId?: string;
    templateSourceHash?: number;
    /**
     * Multi-state preview scenarios stored for this component, surfaced
     * for the AI Assistant so it can reason about cross-state consistency
     * without parsing the source.
     *
     * Read from the Designer-side store on the component model
     * (`Component.getPreviewScenarios`). Omitted entirely when the
     * component has no preview scenarios defined.
     */
    declaredStates?: Array<{id: string; label: string}>;
  } | null;
  selectedVariant: {id: string; displayName: string} | null;
  /**
   * Active frame's multi-state preview scenario. Frame-level (mirrors
   * `selectedVariant`), so the agent knows which scenario the user is
   * looking at even when no inner element is selected. Omitted for
   * single-state flows.
   */
  selectedState?: {id: string; label: string} | null;
  vccStyleOverrides?: Array<{
    nodeId: string;
    tagName: string;
    textContent: string;
    originalStyles: string;
    modifiedStyles: string;
    variant?: {id: string; displayName: string};
    /**
     * Multi-state preview scenario the override was drafted in. Lets the AI
     * Assistant scope the style edit to the right JSX branch when the
     * user adjusts properties inside a scenario frame.
     */
    componentState?: {id: string; label: string};
    breakpointId?: string;
    pseudoState?: string | null;
  }>;
  vccTextOverrides?: Array<{
    nodeId: string;
    tagName: string;
    originalText: string;
    modifiedText: string;
    variant?: {id: string; displayName: string};
    /**
     * Mirrors the `componentState` field on `vccStyleOverrides` — set
     * when the inline text edit happened inside a multi-state preview
     * scenario frame.
     */
    componentState?: {id: string; label: string};
  }>;
  selectedCodeComponentNode: {
    nodeId: string;
    tagName: string;
    textContent: string;
    /**
     * Identifies the multi-state preview scenario the click originated. When
     * present, `nodeId` is relative to the per-state wrapper.
     */
    componentState?: {
      id: string;
      label: string;
    };
  } | null;
  breakpoints: AssistantBreakpoint[];
  cmsCollections?: AssistantCmsCollection[];
  /**
   * Site-scoped Code Functions visible to the current VCC session. Index of
   * `id`, `path`, and an optional one-line `description`. The agent reads
   * bodies via `read-remote-file`.
   */
  siteFunctions?: Array<{id: string; path: string; description?: string}>;
  campaignBrief?: string;
  campaignComponentTask?: CampaignComponentTask;
  /**
   * Set when the user is working inside a campaign (CreateCampaignModal /
   * CampaignOverviewPanel). The structured brief is supplied with each turn
   * so the assistant can make page edits with the current campaign context.
   * `landingPageTargetLanguage` mirrors the campaign landing page's target
   * language (absent = English) so generated page/component copy is written
   * in the campaign's language; chat replies still follow the user's language.
   */
  campaign?: {
    id: string;
    structuredBrief?: StructuredBrief;
    landingPageTargetLanguage?: CampaignPageTargetLanguage;
  } | null;
  /**
   * UI ability signals for Campaign sites — see {@link CampaignUiCapabilities}.
   * Gated on the site kind (`campaign`), not on the active campaign id, so it
   * stays present while no campaign is loaded. Omitted entirely for ordinary
   * sites.
   */
  campaignUiCapabilities?: CampaignUiCapabilities;
}

export interface AIAssistantStylePreset {
  _id: string;
  siteId: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
}

export type SiteLevelInstructions = {
  instructions?: string;
};

/**
 * Version dimension type - defines the isolated state dimensions tracked for staleness
 */
export type VersionDimension = 'dom' | 'styles' | 'variables' | 'ix3';

/**
 * Map of write tool IDs to the dimensions they affect.
 * Tools may write multiple dimensions (e.g. `render` accepts both HTML
 * and CSS so it modifies `dom` and `styles`). The staleness guard blocks a
 * write tool when ANY of its target dimensions is stale.
 */
export const TOOL_DIMENSION_MAP: Record<string, VersionDimension[]> = {
  // DOM-only writes
  'set-element-text-content': ['dom'],
  'set-form-name': ['dom'],
  'set-form-input-required': ['dom'],
  'set-form-input-type': ['dom'],
  'set-form-input-name': ['dom'],
  'set-form-settings': ['dom'],

  // Combined HTML + CSS writes
  render: ['dom', 'styles'],

  // Style-only writes
  'render-css': ['styles'],

  // Variable writes
  'render-variables': ['variables'],
};

/**
 * Get the dimensions affected by a write tool.
 *
 * @param toolId - Tool identifier to look up.
 * @returns The dimensions the tool writes to. Empty array for non-write tools.
 */
export function getToolDimensions(toolId: string): VersionDimension[] {
  return TOOL_DIMENSION_MAP[toolId] ?? [];
}

/**
 * Mutation tools that produce Designer state changes but are NOT tracked in
 * TOOL_DIMENSION_MAP. These tools mutate state without going through the
 * version-tracked write paths (no DOM/styles/variables version is bumped
 * before they run), so the staleness guard would reject every call.
 *
 * Consumers that need a write-tool predicate (attribution flagging,
 * write-tool classification) MUST use {@link isMutationTool} instead of
 * checking `TOOL_DIMENSION_MAP[toolId]` directly — that map only covers a
 * subset.
 */
export const ADDITIONAL_MUTATION_TOOL_IDS: ReadonlySet<string> = new Set([
  'delete-unused-styles',
]);

/**
 * Returns true if the tool mutates Designer state. Use this as the
 * write-tool predicate for attribution and write-tool classification.
 * The dimension map alone is incomplete — see {@link ADDITIONAL_MUTATION_TOOL_IDS}.
 */
export function isMutationTool(toolId: string): boolean {
  return (
    getToolDimensions(toolId).length > 0 ||
    ADDITIONAL_MUTATION_TOOL_IDS.has(toolId)
  );
}

/**
 * Refresh tools for each dimension.
 * These are the tools the AI should call to refresh its context for a dimension.
 *
 * Some tools refresh multiple dimensions (e.g. `read-page` returns both HTML
 * and applicable CSS, so it appears in both `dom` and `styles`).
 *
 * Form getters are intentionally NOT listed here: they return scalar
 * properties of a single element, which is not enough context to safely
 * unblock a broad write like `render`.
 */
export const REFRESH_TOOLS_BY_DIMENSION: Record<VersionDimension, string[]> = {
  dom: [
    'read-page',
    'read-page-tree',
    'get-html-from-selected-element',
    'get-html-from-query-selectors',
    'get-html-from-text',
  ],
  styles: [
    'read-page',
    'get-html-from-selected-element',
    'get-html-from-query-selectors',
    'get-html-from-text',
    'search-styles',
  ],
  variables: ['search-variables'],
  ix3: [], // No IX3 read tools yet
};

/**
 * Inverse of REFRESH_TOOLS_BY_DIMENSION: tool ID -> dimensions it refreshes.
 * Derived once at module load so call sites don't need to re-iterate.
 */
const READ_TOOL_DIMENSIONS: Record<string, VersionDimension[]> = (() => {
  const out: Record<string, VersionDimension[]> = {};
  for (const [dim, tools] of Object.entries(REFRESH_TOOLS_BY_DIMENSION) as [
    VersionDimension,
    string[],
  ][]) {
    for (const tool of tools) {
      (out[tool] ??= []).push(dim);
    }
  }
  return out;
})();

export type StalenessRecoveryDetails = {
  suggestion: string;
  refreshTools: string[];
};

/**
 * Get the dimensions a tool refreshes (read tools) or writes to (write tools).
 * Used by the persistence layer to decide which fields of an incoming
 * `versionInfo` payload should be written to `lastReadVersions`.
 *
 * @param toolId - Tool identifier to look up.
 * @returns The dimensions whose `lastReadVersions` should be updated when
 *   this tool's callback arrives. Empty for tools that affect none of the
 *   tracked dimensions (e.g. `calculate`, file tools).
 */
export function getToolPersistableDimensions(
  toolId: string
): VersionDimension[] {
  const readDims = READ_TOOL_DIMENSIONS[toolId];
  if (readDims) return readDims;
  return getToolDimensions(toolId);
}

/**
 * Get the read tools that refresh the full stale dimension set.
 *
 * @param dimensions - Stale dimensions the agent must refresh before retrying.
 * @returns Recommended read tools for the complete stale dimension set.
 */
export function getRefreshToolsForDimensions(
  dimensions: VersionDimension[]
): string[] {
  const hasDom = dimensions.includes('dom');
  const hasStyles = dimensions.includes('styles');
  const hasVariables = dimensions.includes('variables');
  const refreshTools: string[] = [];

  // read-page refreshes both DOM and styles, so prefer it whenever both
  // dimensions are stale. read-page-tree would only refresh DOM.
  if (hasDom && hasStyles) {
    refreshTools.push('read-page');
  } else {
    if (hasDom) {
      refreshTools.push('read-page-tree');
    }
    if (hasStyles) {
      refreshTools.push('search-styles');
    }
  }

  if (hasVariables) {
    refreshTools.push('search-variables');
  }

  return refreshTools;
}

/**
 * Builds LLM-facing instructions for recovering from stale version dimensions.
 *
 * @param dimensions - Stale dimensions the agent must refresh before retrying.
 * @returns A suggestion string and recommended read-tool sequence for recovery.
 */
export function buildStalenessRecoveryDetails(
  dimensions: VersionDimension[]
): StalenessRecoveryDetails {
  const uniqueDimensions = [...new Set(dimensions)];
  const dimensionList = uniqueDimensions.join(', ');
  const refreshTools = getRefreshToolsForDimensions(uniqueDimensions);
  const suggestion = `Refresh all stale dimensions before retrying: ${dimensionList}. Call: ${refreshTools.join(', ')}.`;

  return {
    suggestion,
    refreshTools,
  };
}

/**
 * Filter a `versionInfo` payload to only the listed dimensions. Preserves
 * `turnId` only when at least one tracked dimension is retained so tools with
 * no staleness-relevant state cannot persist turn-only baselines.
 *
 * @param versionInfo - Full or partial version info from the frontend.
 * @param dims - Dimensions to keep.
 * @returns A new partial version info containing only the requested
 *   dimensions plus `turnId`.
 */
export function filterVersionInfoByDimensions(
  versionInfo: Partial<VersionInfo>,
  dims: VersionDimension[]
): Partial<VersionInfo> {
  const filtered: Partial<VersionInfo> = {};
  for (const dim of dims) {
    if (versionInfo[dim] !== undefined) {
      filtered[dim] = versionInfo[dim];
    }
  }
  if (Object.keys(filtered).length > 0 && versionInfo.turnId !== undefined) {
    filtered.turnId = versionInfo.turnId;
  }
  return filtered;
}

/**
 * Version information for a single dimension (DOM, Styles, or Variables)
 */
export interface DimensionVersion {
  version: number | string; // number for DOM/Styles, string for variables fingerprint
}

/**
 * Version information for all three dimensions
 * Used for staleness detection in the AI Assistant
 */
export interface VersionInfo {
  dom: DimensionVersion;
  styles: DimensionVersion;
  variables: DimensionVersion;
  ix3: DimensionVersion;
  turnId?: string; // The ID of the user message that started this turn
}
